import {
  isOccupyingNow,
  resolveRemindersEnabled,
} from "@/hooks/v2/useProperties";
import type { OwnerPayoutGroup } from "@/lib/owner-payouts";
import type { RentLedgerWithProofs } from "@/lib/rent-ledger-fetch";
import { selectOpenPastMonthRent } from "@/lib/rent-past-months";
import {
  currentPeriodDueDate,
  lateRentStage,
} from "@/lib/rent-reminder-templates";
import type { BrainItem, BrainText } from "./types";

/**
 * Why the engine wants a human, by the code before ":" in review_reason
 * (bank-reconcile's ReviewReason, plus finAPI's review_first). The note after
 * the colon stays off the card. An unknown code shows no reason.
 */
const REVIEW_REASON = new Map<string, BrainText>(
  Object.entries({
    partial_amount: {
      key: "brain.recon.reason.partial",
      fallback: "Less than the open rent",
    },
    overpayment: {
      key: "brain.recon.reason.overpayment",
      fallback: "More than the open rent",
    },
    unknown_sender: {
      key: "brain.recon.reason.unknownSender",
      fallback: "Sender not recognised",
    },
    duplicate_transfer: {
      key: "brain.recon.reason.duplicate",
      fallback: "No open rent for this tenant: possibly paid twice",
    },
    ambiguous: {
      key: "brain.recon.reason.ambiguous",
      fallback: "Several open invoices fit",
    },
    review_first: {
      key: "brain.recon.reason.reviewFirst",
      fallback: "Confident match, waiting for your confirmation",
    },
  })
);

/**
 * The `OwnerListRow` fields the payout cards read. The caller passes exactly
 * the rows the Owners page's "Payouts ready" tile counts (test owners hidden,
 * the platform lessor included), so the two always agree.
 */
export interface PaymentOwnerRow {
  id: string;
  name?: string | null;
  bank_holder?: string | null;
  bank_iban?: string | null;
  bank_bic?: string | null;
  payoutGroups: OwnerPayoutGroup[];
  carriedOverPayoutGroups: OwnerPayoutGroup[];
  propertyList?: { id: number | string; title: string | null }[];
}

export interface PaymentDeriveInput {
  owners: PaymentOwnerRow[];
  matches: {
    id: string;
    status: string;
    payment_id: string | null;
    /** NULL = the whole bank transaction (COL-508). */
    amount_cents?: number | null;
    review_reason: string | null;
    created_at: string | null;
    tenantName?: string | null;
    tenantEmail?: string | null;
    bank_transactions?: {
      amount_cents?: number | null;
      counterpart_name?: string | null;
      purpose?: string | null;
      booking_date?: string | null;
    } | null;
    /** The suggested invoice (the `payment_id` embed); null = no target. */
    payments?: {
      due_date?: string | null;
      amount_cents?: number | null;
      bookings?: {
        rooms?: {
          title?: string | null;
          properties?: { id: number; title?: string | null } | null;
        } | null;
      } | null;
    } | null;
  }[];
  /** Open bookings with the columns lateRentStage and isOccupyingNow read. */
  bookings: PaymentBooking[];
  /**
   * The rent ledger of `trackedRentBookings(bookings)`, for open past months.
   * null = not known (loading or failed): the late-rent roll-up is withheld.
   */
  ledger: RentLedgerWithProofs | null;
}

export type PaymentBooking = Parameters<typeof lateRentStage>[0] & {
  id: number;
  status?: string | null;
  move_in_date?: string | null;
  move_out_date?: string | null;
  name?: string | null;
  monthly_rent?: number | string | null;
  accumulated_fees_eur?: number | string | null;
  rooms?: {
    apartments?: { rent_reminders_enabled?: boolean | null } | null;
    properties?: {
      title?: string | null;
      rent_reminders_enabled?: boolean | null;
    } | null;
  } | null;
  [k: string]: unknown;
};

/**
 * The tenancies the Reminders tab tracks: occupying now, with reminders on
 * for their unit (apartment override, else property, else on).
 */
export function trackedRentBookings(
  bookings: readonly PaymentBooking[],
  now: Date
): PaymentBooking[] {
  return bookings.filter(
    (b) =>
      isOccupyingNow(
        b as unknown as Parameters<typeof isOccupyingNow>[0],
        now
      ) &&
      resolveRemindersEnabled({
        apartment: b.rooms?.apartments,
        property: b.rooms?.properties,
      })
  );
}

export function derivePaymentItems(
  input: PaymentDeriveInput,
  now: Date
): BrainItem[] {
  const out: BrainItem[] = [];

  for (const o of input.owners) {
    const ownerHref = `/admin/owners?owner=${o.id}`;
    for (const g of [...o.payoutGroups, ...o.carriedOverPayoutGroups]) {
      if (!g.ready) continue;
      const propertyId = Number(g.propertyId);
      const property = o.propertyList?.find((p) => Number(p.id) === propertyId);
      out.push({
        key: `payout_ready:${o.id}:${g.propertyId}:${g.periodMonth}`,
        kind: "payout_ready",
        since: null,
        who: { label: o.name || o.id, href: ownerHref },
        where: property
          ? {
              label: property.title || `#${property.id}`,
              href: `/admin/properties/${property.id}`,
            }
          : undefined,
        facts: [
          {
            label: { key: "brain.fact.period", fallback: "Period" },
            month: g.periodMonth,
          },
          {
            label: { key: "brain.fact.collected", fallback: "Rent collected" },
            eur: g.collectedEur,
          },
          {
            label: { key: "brain.fact.payout", fallback: "Payout" },
            eur: g.livePayoutEur,
          },
        ],
        primaryHref: ownerHref,
        refs: { lessorId: o.id, propertyId },
        payout: {
          lessorId: o.id,
          propertyId,
          periodMonth: g.periodMonth,
          payoutEur: g.livePayoutEur,
          collectedEur: g.collectedEur,
          bankHolder: o.bank_holder ?? null,
          bankIban: o.bank_iban ?? null,
          bankBic: o.bank_bic ?? null,
          unpaidInvoiceIds: g.unpaidInvoiceIds ?? [],
        },
      });
    }
  }

  for (const r of input.matches) {
    if (r.status !== "needs_review") continue;
    const tx = r.bank_transactions;
    // Same rule as the review page's availableCents().
    const amountCents = r.amount_cents ?? tx?.amount_cents ?? 0;
    // Where Confirm would book the money — the review page's confirm dialog
    // shows the same: tenant, unit, rent month · amount.
    const p = r.payment_id ? r.payments : null;
    const room = p?.bookings?.rooms;
    const property = room?.properties;
    const tenant = p ? r.tenantName || r.tenantEmail : null;
    const reason = REVIEW_REASON.get(r.review_reason?.split(":", 1)[0] ?? "");
    out.push({
      key: `reconcile:${r.id}`,
      kind: "reconcile",
      since: r.created_at,
      who: {
        label: tx?.counterpart_name || r.tenantName || "—",
        href: "/admin/reconciliation",
      },
      where: property
        ? {
            label: [property.title, room?.title].filter(Boolean).join(" · "),
            href: `/admin/properties/${property.id}`,
          }
        : undefined,
      // No summary line: review_reason is the engine's note (English, in
      // cents), and for a partial payment it describes the tenant's OLDEST open
      // invoice — one review row per candidate shares it, so it would name a
      // different invoice than this card's Rent. Only its code is shown.
      facts: [
        ...(reason
          ? [
              {
                label: { key: "brain.fact.reason", fallback: "Reason" },
                parts: [reason],
              },
            ]
          : []),
        {
          label: { key: "brain.fact.amount", fallback: "Amount" },
          eur: amountCents / 100,
        },
        ...(tenant
          ? [
              {
                label: { key: "brain.fact.tenant", fallback: "Tenant" },
                value: tenant,
              },
            ]
          : []),
        ...(p
          ? [
              {
                label: { key: "brain.fact.rent", fallback: "Rent" },
                month: p.due_date?.slice(0, 7) ?? "—",
                eur: (p.amount_cents ?? 0) / 100,
              },
            ]
          : []),
        ...(tx?.booking_date
          ? [
              {
                label: { key: "brain.fact.booked", fallback: "Booked" },
                date: tx.booking_date,
              },
            ]
          : []),
        ...(tx?.purpose
          ? [
              {
                label: { key: "brain.fact.reference", fallback: "Reference" },
                value: tx.purpose,
              },
            ]
          : []),
      ],
      primaryHref: "/admin/reconciliation",
      refs: { matchId: r.id },
      recon: {
        matchId: r.id,
        amountCents,
        // No embed, no Confirm: never book money to an invoice nobody can see.
        suggestedPaymentId: p ? r.payment_id : null,
        counterpartName: tx?.counterpart_name ?? null,
        purpose: tx?.purpose ?? null,
        reviewReason: r.review_reason,
      },
    });
  }

  // The Reminders tab's "Overdue" view, in its unit (rows): each tracked
  // tenancy late on its current period, plus each open past month (COL-435).
  const tracked = input.ledger ? trackedRentBookings(input.bookings, now) : [];
  const lateNow = tracked.filter((b) => lateRentStage(b) > 0);
  const pastMonths = input.ledger
    ? selectOpenPastMonthRent({
        rows: input.ledger.ledger,
        proofFor: input.ledger.proofFor,
        rentDueDayByBooking: new Map(
          tracked.map((b) => [b.id, b.rent_due_date ?? null])
        ),
        bookingIds: new Set(tracked.map((b) => b.id)),
        now,
      })
    : [];
  const late = lateNow.length + pastMonths.length;
  // One row per tenant: property, tenant, what's open, late fees.
  // ponytail: the current period counts monthly_rent (discounts ignored) —
  // read its ledger row instead if the totals ever disagree with Reminders.
  const byBooking = new Map<
    number,
    NonNullable<BrainItem["lateRent"]>[number]
  >();
  const rowFor = (id: number) => {
    const b = tracked.find((t) => t.id === id);
    let row = byBooking.get(id);
    if (!row) {
      row = {
        bookingId: id,
        tenant: b?.name ?? null,
        property: b?.rooms?.properties?.title ?? null,
        openEur: 0,
        feesEur: Number(b?.accumulated_fees_eur) || 0,
        dueSince: null,
      };
      byBooking.set(id, row);
    }
    return row;
  };
  const since = (row: { dueSince: string | null }, iso: string) => {
    if (!row.dueSince || iso < row.dueSince) row.dueSince = iso;
  };
  for (const b of lateNow) {
    const row = rowFor(b.id);
    row.openEur += Number(b.monthly_rent) || 0;
    // lateRentStage > 0 implies rent_due_date is set.
    since(
      row,
      currentPeriodDueDate(b.rent_due_date!, now).toISOString().slice(0, 10)
    );
  }
  for (const m of pastMonths) {
    const row = rowFor(m.booking_id);
    row.openEur += m.open_eur;
    since(row, m.due_date);
  }
  const lateRent = [...byBooking.values()].sort(
    (a, b) =>
      (a.property ?? "").localeCompare(b.property ?? "") ||
      (a.tenant ?? "").localeCompare(b.tenant ?? "")
  );
  if (late > 0) {
    out.push({
      key: "late_rent",
      kind: "late_rent",
      since: null,
      // No summary: the "Late payments" fact already carries the count.
      facts: [
        {
          label: { key: "brain.fact.latePayments", fallback: "Late payments" },
          value: String(late),
        },
      ],
      primaryHref: "/admin?tab=reminders",
      refs: {},
      rollupCount: late,
      lateRent,
    });
  }
  return out;
}
